import { Wrench, Phone, HardHat, AlertCircle, Shield, Building2 } from "lucide-react";
import GenericPracticePage from "@/components/GenericPracticePage";

const IndustrialAccident = () => {
  return (
    <GenericPracticePage
      title="Industrial Accident Attorney"
      subtitle="Workplace injuries at construction sites, refineries, and industrial facilities require experienced legal representation. We fight for injured workers' rights and maximum compensation."
      icon={<Wrench className="h-20 w-20 mx-auto mb-6 text-accent drop-shadow-lg" />}
      mainContent={
        <div className="space-y-8">
          <section>
            <h2 className="text-3xl font-bold mb-4">Houston Industrial Accident Lawyer</h2>
            <p className="text-lg text-muted-foreground mb-4">
              Houston's industrial sector - including oil refineries, chemical plants, construction sites, and manufacturing facilities - provides thousands of jobs but also presents serious safety risks. When employers cut corners on safety or fail to provide proper equipment and training, workers suffer devastating injuries.
            </p>
            <p className="text-lg text-muted-foreground">
              Workers' compensation may not be your only option. Many injured workers have additional claims against negligent third parties, equipment manufacturers, or employers who intentionally violated safety regulations.
            </p>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Types of Industrial Accidents</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-4 border rounded-lg">
                <HardHat className="h-8 w-8 text-accent mb-2" />
                <h3 className="font-bold mb-2">Construction Site Accidents</h3>
                <p className="text-sm text-muted-foreground">Falls, scaffolding collapses, equipment accidents, and struck-by incidents</p>
              </div>
              <div className="p-4 border rounded-lg">
                <AlertCircle className="h-8 w-8 text-accent mb-2" />
                <h3 className="font-bold mb-2">Refinery & Chemical Plant</h3>
                <p className="text-sm text-muted-foreground">Explosions, fires, toxic exposure, and catastrophic equipment failures</p>
              </div>
              <div className="p-4 border rounded-lg">
                <Building2 className="h-8 w-8 text-accent mb-2" />
                <h3 className="font-bold mb-2">Manufacturing Injuries</h3>
                <p className="text-sm text-muted-foreground">Machine entanglement, crushing injuries, repetitive stress injuries</p>
              </div>
              <div className="p-4 border rounded-lg">
                <Shield className="h-8 w-8 text-accent mb-2" />
                <h3 className="font-bold mb-2">Equipment Malfunctions</h3>
                <p className="text-sm text-muted-foreground">Defective machinery, forklift accidents, crane collapses</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Common Industrial Injuries</h2>
            <div className="bg-gradient-subtle p-6 rounded-lg space-y-3">
              <p className="text-muted-foreground">• <strong>Burn Injuries:</strong> Chemical burns, thermal burns, and electrical burns causing permanent scarring and disability</p>
              <p className="text-muted-foreground">• <strong>Amputations:</strong> Loss of limbs or digits from machinery accidents</p>
              <p className="text-muted-foreground">• <strong>Spinal Cord Injuries:</strong> Paralysis from falls or equipment accidents</p>
              <p className="text-muted-foreground">• <strong>Traumatic Brain Injuries:</strong> Head trauma from falls or being struck by objects</p>
              <p className="text-muted-foreground">• <strong>Respiratory Damage:</strong> Toxic exposure to chemicals, asbestos, or other hazardous materials</p>
              <p className="text-muted-foreground">• <strong>Crush Injuries:</strong> Life-threatening injuries from heavy equipment or collapsing structures</p>
              <p className="text-muted-foreground">• <strong>Electrocution:</strong> Serious burns and internal injuries from electrical contact</p>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">Beyond Workers' Compensation</h2>
            <div className="bg-primary/5 border border-primary/20 p-6 rounded-lg">
              <p className="text-muted-foreground mb-4">
                Workers' compensation provides medical benefits and limited wage replacement, but it doesn't compensate you for pain and suffering or hold negligent parties fully accountable. You may have additional claims:
              </p>
              <div className="space-y-3">
                <div className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center">
                    <span className="text-accent font-bold text-sm">1</span>
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">Third-Party Liability Claims</h4>
                    <p className="text-sm text-muted-foreground">Against subcontractors, equipment manufacturers, property owners, or other negligent parties</p>
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center">
                    <span className="text-accent font-bold text-sm">2</span>
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">Product Liability Claims</h4>
                    <p className="text-sm text-muted-foreground">When defective equipment or machinery caused your injury</p>
                  </div>
                </div>
                <div className="flex gap-3 items-start">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center">
                    <span className="text-accent font-bold text-sm">3</span>
                  </div>
                  <div>
                    <h4 className="font-bold mb-1">Gross Negligence Claims</h4>
                    <p className="text-sm text-muted-foreground">If your employer intentionally removed safety guards or willfully violated safety regulations</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-3xl font-bold mb-6">What We Investigate</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="border-l-4 border-accent pl-4">
                <h3 className="font-bold mb-2">OSHA Violations</h3>
                <p className="text-sm text-muted-foreground">Safety regulation violations that contributed to your accident</p>
              </div>
              <div className="border-l-4 border-accent pl-4">
                <h3 className="font-bold mb-2">Training Records</h3>
                <p className="text-sm text-muted-foreground">Whether you received proper safety training and equipment</p>
              </div>
              <div className="border-l-4 border-accent pl-4">
                <h3 className="font-bold mb-2">Equipment Maintenance</h3>
                <p className="text-sm text-muted-foreground">Maintenance logs and inspection records for defective equipment</p>
              </div>
              <div className="border-l-4 border-accent pl-4">
                <h3 className="font-bold mb-2">Multiple Liable Parties</h3>
                <p className="text-sm text-muted-foreground">All contractors, subcontractors, and companies sharing responsibility</p>
              </div>
            </div>
          </section>

          <section className="bg-accent/5 p-6 rounded-lg">
            <h2 className="text-2xl font-bold mb-4">Your Rights as an Injured Worker</h2>
            <ul className="space-y-2 text-muted-foreground">
              <li className="flex gap-2">
                <span className="text-accent font-bold">✓</span>
                <span>You have the right to pursue claims beyond workers' compensation</span>
              </li>
              <li className="flex gap-2">
                <span className="text-accent font-bold">✓</span>
                <span>Your employer cannot retaliate against you for filing a claim</span>
              </li>
              <li className="flex gap-2">
                <span className="text-accent font-bold">✓</span>
                <span>You deserve full compensation for all damages, including pain and suffering</span>
              </li>
              <li className="flex gap-2">
                <span className="text-accent font-bold">✓</span>
                <span>You can hold negligent third parties accountable for their actions</span>
              </li>
            </ul>
          </section>

          <section className="text-center bg-gradient-subtle p-8 rounded-lg">
            <Phone className="h-12 w-12 mx-auto mb-4 text-accent" />
            <h2 className="text-2xl font-bold mb-4">Injured on the Job?</h2>
            <p className="text-lg text-muted-foreground mb-6">
              Don't settle for just workers' comp. Contact us for a case evaluation to learn about all your legal options. No fee unless we win.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:8329303059" className="text-2xl font-bold text-accent hover:text-accent/80">
                (832) 930-3059
              </a>
            </div>
          </section>
        </div>
      }
    />
  );
};

export default IndustrialAccident;
